import { forwardRef } from '@heroui/system';
import { MdOutlineClose, MdOutlineSearch } from 'react-icons/md';
import { Input, InputProps } from '@heroui/react';

type SearchInputProps = {
  inputWrapperClassName?: string;
  inputClassName?: string;
  inputVariant?: 'primary' | 'secondary';
} & InputProps;

const customClearButton = (
  <span className="clear-button">
    <MdOutlineClose className="fill-base-20" />
  </span>
);

const SearchInput = forwardRef<'input', SearchInputProps>((props, ref) => {
  const {
    isClearable,
    startContent,
    label,
    inputWrapperClassName,
    inputClassName,
    inputVariant = 'primary',
    value,
    onClear,
    ...restProps
  } = props;

  const inputProps = restProps as Omit<InputProps, 'ref' | 'isClearable' | 'startContent' | 'label'>;
  const inputBackgroundColor = inputVariant === 'primary' ? 'bg-base-50' : 'bg-base-60';
  const showClearBtn = isClearable && !!value;

  return (
    <Input
      ref={ref}
      value={value}
      startContent={<MdOutlineSearch size={22} className="fill-current tmdx-input__start-icon" />}
      endContent={showClearBtn ? customClearButton : undefined}
      isClearable={showClearBtn}
      onClear={showClearBtn ? onClear : undefined}
      radius="full"
      spellCheck="false"
      classNames={{
        base: ['tmdx-input', 'tmdx-input-search', 'group'],
        inputWrapper: [
          inputBackgroundColor,
          'group-data-[focus=true]:bg-base-50!',
          'group-data-[focus=true]:ring-0!',
          'group-data-[focus=true]:ring-offset-0!',
          'group-data-[invalid=true]:bg-red-80',
          'tmdx-input__wrapper',
          'transition-none!',
          startContent && 'hasStartContent',
          !label && 'min-h-9 h-9',
          !!label && 'hasLabel',
          'shadow-none',
          'border',
          'border-solid',
          'border-base-30',
          inputWrapperClassName,
        ],
        input: [
          'text-base-5',
          'placeholder:text-base-20',
          'text-m-m!',
          'group-data-[invalid=true]:text-base-5!',
          'tmdx-input__item',
          inputClassName,
        ],
        label: [
          'text-base-20',
          'text-s-r!',
          'tmdx-input__label',
          'group-focus-within:text-base-10',
          'group-data-[invalid=true]:text-danger!',
        ],
        helperWrapper: 'empty:hidden!',
        description: ['text-base-20', 'text-s-r', 'group-data-[invalid=true]:text-danger', 'form-item__description'],
        errorMessage: ['text-s-r', 'group-data-[invalid=true]:text-danger'],
        innerWrapper: ['bg-transparent', 'text-base-30', 'tmdx-input__inner-wrapper'],
        clearButton: ['opacity-100!'],
      }}
      {...inputProps}
    />
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
